import { useContext, useCallback, useEffect, useState } from 'react';
import FormModal from '../../Shared/form/modal/FormModal';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import { FormContext, FormSchemaFields } from '@stormwater-software/lib-form';
import { regionApi } from '../../../utils/api/region';
import divisionApi from '../../../utils/api/division';
import reportApi from '../../../utils/api/report';
import React from 'react';

const getProjectOptions = (availableClients: any[], selectedClients: any[]) => {
  availableClients.sort((a: any, b: any) => a.name.localeCompare(b.name));

  if (!selectedClients || selectedClients.length === 0) return [];

  const selectedClientIds = selectedClients.map((c: any) => c.id);
  let clients = availableClients.filter((ac: any) =>
    selectedClientIds.includes(ac.id),
  );

  if (selectedClientIds.length === 1 && selectedClientIds[0] === 0) {
    clients = availableClients;
  }

  return clients.reduce(
    (acc: any[], client: any) => acc.concat(client.projects || []),
    [],
  );
};

const getSchema = (formData: any) => {
  const formContext = useContext(FormContext);
  const { divisions = [], regions = [], clients = [] } = formData;

  const schema: any = {
    regionId: {
      controlType: UIControlType.select,
      openOnFocus: true,
      labelKey: 'name',
      valueKey: 'id',
      label: 'Select Region',
      options: regions,
      noOptionMessage: 'No Regions found',
      placeholder: 'Search for Regions',
    },
    divisionIds: {
      controlType: UIControlType.customTagSelect,
      labelKey: 'name',
      valueKey: 'id',
      isMulti: true,
      openOnFocus: true,
      showSelectAll: true,
      label: 'Select Division(s)',
      options: divisions,
      noOptionMessage: 'No Divisions found',
      showClearAll: true,
      placeholder: 'Search for Divisions',
      hideBadgesOnSelectAll: true,
    },
    clients: {
      controlType: UIControlType.customTagSelect,
      labelKey: 'name',
      valueKey: 'id',
      isMulti: true,
      openOnFocus: true,
      label: 'Select Client(s)',
      options: clients,
      noOptionMessage: 'No Clients found',
      placeholder: 'Search for Clients',
      showSelectAll: true,
      hideBadgesOnSelectAll: true,
      showClearAll: true,
    },
    projects: {
      controlType: UIControlType.customTagSelect,
      labelKey: 'name',
      valueKey: 'id',
      isMulti: true,
      openOnFocus: true,
      showSelectAll: true,
      hideBadgesOnSelectAll: true,
      label: 'Select Project(s)',
      options:
        (clients && getProjectOptions(clients, formContext.value.clients)) || [],
      noOptionMessage: 'No Projects found',
      placeholder: 'Search for Projects',
      showClearAll: true,
    },
  };

  if (regions.length === 0) {
    delete schema.regionId;
  }

  return schema;
};

const ProjectsReportModal = ({
  setShowModal,
  setData,
  setFormData,
  formData,
  isEdit,
}: {
  setShowModal: Function;
  setData: Function;
  setFormData: Function;
  formData: any;
  isEdit: boolean;
}) => {
  const formContext = useContext(FormContext);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([regionApi.index()]).then(([regionsData]) => {
      const newFormData = { ...formData };

      if (regionsData) {
        newFormData.regions = regionsData.length
          ? [{ id: 0, name: 'Select all' }, ...regionsData]
          : [];
        formContext.set('regionId', '0');
      }

      setFormData(newFormData);
    });
  }, []);

  useEffect(() => {
    if (formContext.value.regionId && String(formContext.value.regionId).length) {
      setDivisions(formContext.value.regionId);
    }
  }, [formContext.value.regionId]);

  useEffect(() => {
    if (formContext.value.divisionIds) {
      setClients();
    }
  }, [formContext.value.divisionIds]);

  const setDivisions = useCallback(
    (regionId: any) => {
      regionApi.divisions
        .index(regionId)
        .then((data: any) => {
          setFormData((prev: any) => ({ ...prev, divisions: data }));

          if (formContext.value.divisionIds?.length) {
            const allSelected =
              formContext.value.divisionIds.length === 1 &&
              formContext.value.divisionIds[0].id === 0;

            formContext.set(
              'divisionIds',
              allSelected
                ? formContext.value.divisionIds
                : formContext.value.divisionIds.filter((d: any) =>
                    data.map((_: any) => _.id).includes(d.id),
                  ),
            );
          } else {
            formContext.set('divisionIds', []);
          }
        })
        .catch(console.error);
    },
    [formContext.value.regionId],
  );

  const setClients = useCallback(async () => {
    if (!formContext.value.divisionIds?.length) return;

    let divisionIds = formContext.value.divisionIds.map((d: any) => d.id);

    if (divisionIds.length === 1 && divisionIds[0] === 0) {
      divisionIds = formData.divisions.map((d: any) => d.id);
    }

    const clients =
      await divisionApi.clients.getClientsForMultipleDivisions(divisionIds);

    setFormData((prev: any) => ({ ...prev, clients: clients || [] }));

    if (formContext.value.clients?.length) {
      const allSelected =
        formContext.value.clients.length === 1 &&
        formContext.value.clients[0].id === 0;

      formContext.set(
        'clients',
        allSelected
          ? formContext.value.clients
          : formContext.value.clients.filter((c: any) =>
              clients.map((_: any) => _.id).includes(c.id),
            ),
      );
    } else {
      formContext.set('clients', []);
    }
  }, [formContext.value.divisionIds]);

  const submit = async () => {
    setIsSubmitting(true);

    let regionIds: number[] = [];
    let divisionIds: number[] = formContext.value.divisionIds?.map((d: any) => d.id) || [];
    let clientIds: number[] = formContext.value.clients?.map((c: any) => c.id) || [];
    let projectIds: number[] = formContext.value.projects?.map((p: any) => p.id) || [];

    if (
      formContext.value.regionId &&
      Number(formContext.value.regionId) !== 0
    ) {
      regionIds = [Number(formContext.value.regionId)];
    }

    if (divisionIds.length === 1 && divisionIds[0] === 0) {
      divisionIds = formData.divisions.map((d: any) => d.id);
    }

    if (clientIds.length === 1 && clientIds[0] === 0) {
      clientIds = formData.clients.map((c: any) => c.id);
    }

    if (projectIds.length === 1 && projectIds[0] === 0) {
      projectIds = getProjectOptions(
        formData.clients,
        formContext.value.clients,
      ).map((p: any) => p.id);
    }

    reportApi
      .getProjectsReport({
        regionIds: regionIds.length ? regionIds : undefined,
        divisionIds: divisionIds.length ? divisionIds : undefined,
        clientIds: clientIds.length ? clientIds : undefined,
        projectIds: projectIds.length ? projectIds : undefined,
      })
      .then((data) => {
        setData(data);
        setFormData((prev: any) => ({
          ...prev,
          selectedRegionIds: regionIds,
        }));
        setShowModal(false);
      })
      .catch(console.error)
      .finally(() => setIsSubmitting(false));

    return Promise.resolve();
  };

  const handleClose = () => {
    setShowModal(false);
  };

  return (
    <FormModal
      onSubmit={submit}
      onCancel={handleClose}
      modalProps={{
        title: isEdit ? 'Modify Report' : 'Create Report',
        submitBtnText: isSubmitting ? 'Loading...' : 'Save',
      }}
    >
      <section className='project-form'>
        <section className='container'>
          <FormSchemaFields
            schema={getSchema(formData)}
            onChange={formContext.set}
            formData={formContext.value}
            initialFormData={formContext.value}
          />
        </section>
      </section>
    </FormModal>
  );
};

export default ProjectsReportModal;
